import { Suspense, useMemo, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MONITOR_PARAM, PATTERNS, ROUTES } from '@constants/routes';
import { STORAGE } from '@constants/storage';
import { store } from '@lib/storage';
import { usesCourier, usesOperations, useSession } from '@lib/auth';
import { Forbidden, NotFound, RouteBoundary, SkeletonPage } from '@ui';
import { IdlePrefetch } from './IdlePrefetch';
import { LEGACY_PATTERNS, LegacyRedirect } from './legacyRoutes';
import {
  AccountPage, ADMIN_CHUNKS, HouseholdDetailPage, HouseholdImportPage, HouseholdsPage, OrderDetailPage, OrdersPage, PROGRAM_CHUNKS, ProgramSettingsPage, AlertsPage, COURIER_CHUNKS, CylinderLabelsPage, DeliveriesPage, DeliveryPage, HUB_CHUNKS, HubApplicationDetailPage, HubApplicationsPage, HubVisitPage, HubVisitsPage, VISIT_CHUNKS, AuditPage, CylinderByEpcPage, CylinderDetailPage, CylindersPage, DispatchPage, DispatchSetupPage, GatewayDetailPage, GatewaysPage,
  HomePage, HubCounterPage, HubCouriersPage, HubStockCountPage, HubHouseholdPage, HubHouseholdsPage, HubOrderPage, HubOrdersPage, LocationDetailPage, LocationsPage, LookupPage, OPERATIONS_CHUNKS, OperationsHomePage, OperationsLayout, OverviewPage, ReceiveListPage, ReceivePage, StatisticsPage, TracePage,
  RegisterPage, SHELL_CHUNKS, ShipmentDetailPage, ShipmentPrintPage, ShipmentsPage, TestPage, UpdatesPage, UsersPage,
} from './pages';
import { Shell } from './Shell';

/** "/" of an admin or Peninjau: Beranda, unless the address or this screen asks for the Ruang Kendali. */
function HomeOrControlRoom() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  const kiosk = p.get(MONITOR_PARAM) === '1' || (p.get(MONITOR_PARAM) !== '0' && store.get<boolean>(STORAGE.overviewMonitor, false));
  if (kiosk || p.has('province') || p.has('regency')) return <Navigate to={`${ROUTES.controlRoom}${search}`} replace />;
  return <HomePage />;
}


/** The one route table: print page, legacy redirects, operations mode, and the laptop shell. Pages load on demand. */
export function AppRoutes() {
  const { me, can } = useSession();
  const operator = usesOperations(me);
  const P = PATTERNS.shell;
  const O = PATTERNS.operations;
  const admin = can('user.read') || can('audit.read') || can('ota.manage');
  const hubs = can('hub_application.read');
  const visits = can('hub_application.visit');
  const courier = usesCourier(me);
  const program = can('household.read') || can('order.read');
  // one array per set of permissions: the idle prefetch runs once, not on every render
  const prefetch = useMemo(() => [...SHELL_CHUNKS, ...(admin ? ADMIN_CHUNKS : []), ...(hubs ? HUB_CHUNKS : []), ...(visits ? VISIT_CHUNKS : []), ...(courier ? COURIER_CHUNKS : []), ...(program && !operator ? PROGRAM_CHUNKS : [])], [admin, hubs, visits, courier, program, operator]);
  // every role lands on its own home: a courier's deliveries, a field verifier's visits, a Pengawas (regulator) on
  // Statistik, an admin or Peninjau on Beranda; a control-room screen (monitor mode, an old "/?monitor=1" bookmark or
  // a "/?province=" link of the Ruang Kendali) keeps opening the Ruang Kendali
  const home = courier ? (
    <Navigate to={ROUTES.deliveries} replace />
  ) : // where a role lands is the server's decision (/auth/me landing: a Pengawas on Statistik)
  me.landing === 'statistics' && can('statistics.read') ? (
    <Navigate to={ROUTES.statistics()} replace />
  ) : can('overview.read') ? (
    <HomeOrControlRoom />
  ) : can('hub_application.visit') ? (
    <Navigate to={ROUTES.hubVisits} replace />
  ) : (
    <Navigate to={ROUTES.account} replace />
  );
  // a known page the role may not open is a 403 state; only an unknown address is the 404 page
  const gate = (ok: boolean, page: ReactNode) => (ok ? page : <Forbidden home={operator ? ROUTES.operations.home : ROUTES.home} />);
  const opsGate = (ok: boolean, page: ReactNode) => (ok ? page : <div className="operations-body"><Forbidden home={ROUTES.operations.home} /></div>);
  return (
    <Routes>
      <Route path={PATTERNS.shipmentPrint} element={<RouteBoundary home={ROUTES.home}><Suspense fallback={<SkeletonPage label="Menyiapkan dokumen…" />}><ShipmentPrintPage /></Suspense></RouteBoundary>} />
      <Route path={PATTERNS.cylinderLabels} element={gate(can('cylinder.read'), <RouteBoundary home={ROUTES.home}><Suspense fallback={<SkeletonPage label="Menyiapkan label…" />}><CylinderLabelsPage /></Suspense></RouteBoundary>)} />
      {LEGACY_PATTERNS.map((p) => (
        <Route key={p} path={p} element={<LegacyRedirect />} />
      ))}
      <Route
        path={PATTERNS.operationsRoot}
        element={operator ? <RouteBoundary home={ROUTES.operations.home}><Suspense fallback={<SkeletonPage />}><IdlePrefetch loaders={OPERATIONS_CHUNKS} /><OperationsLayout /></Suspense></RouteBoundary> : <Navigate to={ROUTES.home} replace />}
      >
        <Route index element={<OperationsHomePage />} />
        <Route path={O.dispatch} element={<DispatchSetupPage />} />
        <Route path={O.dispatchShipment} element={<DispatchPage />} />
        <Route path={O.receive} element={<ReceiveListPage />} />
        <Route path={O.receiveShipment} element={<ReceivePage />} />
        <Route path={O.register} element={<RegisterPage />} />
        <Route path={O.lookup} element={<LookupPage />} />
        <Route path={O.test} element={<TestPage />} />
        <Route path={O.orders} element={opsGate(can('order.manage'), <HubOrdersPage />)} />
        <Route path={O.order} element={opsGate(can('order.manage'), <HubOrderPage />)} />
        <Route path={O.counter} element={opsGate(can('order.manage'), <HubCounterPage />)} />
        <Route path={O.couriers} element={opsGate(can('order.read'), <HubCouriersPage />)} />
        <Route path={O.stockCount} element={opsGate(can('order.manage') || can('location.manage'), <HubStockCountPage />)} />
        <Route path={O.households} element={opsGate(can('household.verify'), <HubHouseholdsPage />)} />
        <Route path={O.household} element={opsGate(can('household.verify'), <HubHouseholdPage />)} />
        {/* the location picker is gone (1 account = 1 location): old links land on the home of operations */}
        <Route path={O.location} element={<Navigate to={ROUTES.operations.home} replace />} />
        <Route path="*" element={<div className="operations-body"><NotFound home={ROUTES.operations.home} /></div>} />
      </Route>
      <Route element={<Shell prefetch={prefetch} />}>
        <Route index element={operator ? <Navigate to={ROUTES.operations.home} replace /> : home} />
        {/* every screen needs its read permission (the backend refuses the rest): a known one without it = 403, unknown = 404 */}
        <Route path={P.controlRoom} element={gate(can('overview.read'), <OverviewPage />)} />
        <Route path={P.statistics} element={gate(can('statistics.read'), <StatisticsPage />)} />
        <Route path={P.trace} element={gate(can('cylinder.read') && !operator, <TracePage />)} />
        <Route path={P.shipments} element={gate(can('shipment.read'), <ShipmentsPage />)} />
        <Route path={P.shipment} element={gate(can('shipment.read'), <ShipmentDetailPage />)} />
        <Route path={P.cylinders} element={gate(can('cylinder.read'), <CylindersPage />)} />
        <Route path={P.cylinderByEpc} element={gate(can('cylinder.read'), <CylinderByEpcPage />)} />
        <Route path={P.cylinder} element={gate(can('cylinder.read'), <CylinderDetailPage />)} />
        <Route path={P.locations} element={gate(can('location.read'), <LocationsPage />)} />
        <Route path={P.location} element={gate(can('location.read'), <LocationDetailPage />)} />
        <Route path={P.gateways} element={gate(can('gateway.read'), <GatewaysPage />)} />
        <Route path={P.gateway} element={gate(can('gateway.read'), <GatewayDetailPage />)} />
        <Route path={P.alerts} element={gate(can('alert.read'), <AlertsPage />)} />
        <Route path={P.account} element={<AccountPage />} />
        <Route path={P.updates} element={gate(can('ota.manage'), <UpdatesPage />)} />
        <Route path={P.users} element={gate(can('user.read'), <UsersPage />)} />
        <Route path={P.audit} element={gate(can('audit.read'), <AuditPage />)} />
        <Route path={P.hubApplications} element={gate(can('hub_application.read'), <HubApplicationsPage />)} />
        <Route path={P.hubApplication} element={gate(can('hub_application.read'), <HubApplicationDetailPage />)} />
        <Route path={P.hubVisits} element={gate(can('hub_application.visit'), <HubVisitsPage />)} />
        <Route path={P.hubVisit} element={gate(can('hub_application.visit'), <HubVisitPage />)} />
        <Route path={P.households} element={gate(can('household.read'), <HouseholdsPage />)} />
        <Route path={P.householdsImport} element={gate(can('household.import'), <HouseholdImportPage />)} />
        <Route path={P.household} element={gate(can('household.read'), <HouseholdDetailPage />)} />
        <Route path={P.orders} element={gate(can('order.read'), <OrdersPage />)} />
        <Route path={P.order} element={gate(can('order.read'), <OrderDetailPage />)} />
        <Route path={P.programSettings} element={gate(can('program_setting.manage'), <ProgramSettingsPage />)} />
        <Route path={P.deliveries} element={gate(can('order.deliver'), <DeliveriesPage />)} />
        <Route path={P.delivery} element={gate(can('order.deliver'), <DeliveryPage />)} />
        <Route path="*" element={<NotFound home={operator ? ROUTES.operations.home : ROUTES.home} />} />
      </Route>
    </Routes>
  );
}
